<script lang="ts">
  // BJL-CHECK: Button (used), Badge (used, route badges), Spinner (used), Tag, Drawer, List.
  // `Tag` is unusable under this theme — catppuccin's Tag.css ships colours and a radius but
  // ZERO layout, so it renders as bare text. `Badge` is fully themed and is the library's
  // documented non-interactive chip, so route badges use it with the MTA colour passed
  // through `colors`. Only the shell stays custom: the same drag-resizable bottom sheet as
  // Sheet.svelte, which Drawer can't express. See artifacts/deviations/BusStopSheet.md.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors } from '../../ui/colors'
  import { cardChrome, panelFrame, ROW_RULE, Type } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { formatCountdown, formatShortClock } from '../../ui/transitTime'
  import { useBackHandler } from '../../hooks/useBackHandler.svelte'
  import { useFavoritesStore } from '../../store/favorites'
  import { api, type BusArrival } from '../../services/api'
  import { storeSelect } from '../../lib/store.svelte'
  import { useResizableSheet } from '../../lib/resizableSheet.svelte'
  import type { TransferContext } from './timetable'
  import type { BusStopInfo } from './stationInfo'
  import TripTimetable from './TripTimetable.svelte'
  import TransferBanner from './TransferBanner.svelte'
  import BusRouteBadge from '../BusRouteBadge.svelte'
  import SectionEyebrow from '../SectionEyebrow.svelte'
  import SheetBackButton from '../SheetBackButton.svelte'

  interface Props {
    stop: BusStopInfo
    onClose: () => void
    /** When the sheet was opened from the nearby-stops list, this returns there instead of
     *  just dismissing. Absent for map-tap opens, where there's nothing to go back to. */
    onBack?: () => void
    /** Reopen the sheet on another stop, chosen from a bus's timetable. Absent when the host
     *  can't re-target the sheet, in which case no transfer affordance is offered. */
    onTransfer?: (stop: BusStopInfo, ctx: TransferContext | null) => void
    /** Set when this stop was reached by transferring: arrivals are filtered to buses the
     *  rider could actually catch once their current vehicle gets them here. */
    transfer?: TransferContext | null
  }

  const { stop, onClose, onBack, onTransfer, transfer }: Props = $props()

  const _wh = typeof window !== 'undefined' ? window.innerHeight : 800
  const SHEET_MIN = 80
  const SHEET_DEFAULT = Math.round(_wh * 0.45)
  const SHEET_MAX = Math.round(_wh * 0.92)

  // A line's detail list covers the next hour. SIRI's StopMonitoring response is already
  // short, so the cap is a safety rail — the horizon is what actually bounds the list.
  const DETAIL_HORIZON_S = 3600
  const DETAIL_LIMIT = 40

  function fmtMin(ts: number | null): string {
    return formatCountdown(ts, { fallback: '--' })
  }

  function fmtClock(ts: number | null): string {
    return formatShortClock(ts)
  }

  // Just this stop's flag, so the star re-renders only when its own flips.
  const isFavorited = storeSelect(useFavoritesStore, (s) =>
    s.favoriteBusStops.some((b) => b.id === stop.id),
  )
  // The whole set, so overview badges and the detail-header toggle stay in sync.
  const favoriteBusRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteBusRoutes)

  let arrivals = $state<BusArrival[] | null>(null)
  let loading = $state(true)
  // Route + direction, so B41 northbound and southbound are distinct entries.
  let selectedKey = $state<string | null>(null)
  // Third level: one specific bus off the selected line, drilled into its remaining stops.
  let selectedBus = $state<{
    vehicleRef: string
    routeId: string
    destinationName?: string
  } | null>(null)

  // Mirrors the header's ‹, one level per press. Both levels can be active at once; the bus
  // handler registered later, so Back reaches it first.
  useBackHandler(() => selectedKey !== null, () => { selectedKey = null })
  useBackHandler(() => selectedBus !== null, () => { selectedBus = null })

  // Resize (mirrors StationSheet)
  const sheet = useResizableSheet({
    minHeight: SHEET_MIN,
    defaultHeight: SHEET_DEFAULT,
    maxHeight: SHEET_MAX,
  })

  // Keyed off the id, not the object: a host that rebuilds it would restart the 20 s poll.
  const stopId = $derived(stop.id)

  // All three drill-down levels share one scroll box, so without this a level inherits the
  // offset of the one that opened it — tap a line near the bottom and its next hour opens
  // parked on the last bus. See the longer note in StationSheet.
  let bodyEl = $state<HTMLDivElement | null>(null)
  const drillKey = $derived(
    `${stopId}|` +
      (selectedBus ? `bus:${selectedBus.vehicleRef}` : selectedKey ? `line:${selectedKey}` : 'overview'),
  )
  $effect(() => {
    drillKey
    if (bodyEl) bodyEl.scrollTop = 0
  })

  $effect(() => {
    const id = stopId
    let live = true
    const fetchArrivals = () => {
      api
        .getBusStopArrivals(id)
        .then((d) => { if (live) arrivals = d.arrivals })
        .catch(() => { if (live) arrivals = [] })
        .finally(() => { if (live) loading = false })
    }
    loading = true
    selectedKey = null
    selectedBus = null
    fetchArrivals()
    const timer = setInterval(fetchArrivals, 20_000)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  const keyOf = (a: BusArrival) => `${a.routeId}|${a.directionRef ?? ''}`

  // A transferring rider can override the filter; a fresh transfer re-arms it.
  let showAll = $state(false)
  $effect(() => {
    transfer
    showAll = false
  })
  const cutoff = $derived(transfer && !showAll ? transfer.arriveAt : null)

  // Read by both the overview and the per-line list, so the transfer filter applies at every
  // level. A bus with no predicted time is kept — unknown isn't "too early".
  const visible = $derived.by(() => {
    if (!arrivals || cutoff == null) return arrivals
    return arrivals.filter((a) => {
      const t = a.arrivalTime ?? a.departureTime
      return t == null || t >= cutoff
    })
  })

  // One entry per route+direction. The backend sorts earliest-first, so the first hit per
  // key is the next bus.
  const lines = $derived.by(() => {
    if (!visible) return []
    const seen = new Map<
      string,
      { key: string; routeId: string; destinationName?: string; next: BusArrival; count: number }
    >()
    for (const a of visible) {
      const key = keyOf(a)
      const existing = seen.get(key)
      if (existing) {
        existing.count++
        continue
      }
      seen.set(key, {
        key,
        routeId: a.routeId,
        destinationName: a.destinationName,
        next: a,
        count: 1,
      })
    }
    return [...seen.values()]
  })

  const selectedBuses = $derived.by(() => {
    if (!visible || !selectedKey) return []
    // From when the rider is actually here, which on a transfer is well into the future.
    const horizonEnd = (cutoff ?? Date.now() / 1000) + DETAIL_HORIZON_S
    return visible
      .filter((a) => keyOf(a) === selectedKey)
      // A bus with no predicted time is still worth listing; drop only those known to fall
      // past the hour.
      .filter((a) => {
        const t = a.arrivalTime ?? a.departureTime
        return t == null || t <= horizonEnd
      })
      .slice(0, DETAIL_LIMIT)
  })

  const selectedLine = $derived(lines.find((l) => l.key === selectedKey) ?? null)
  const selectedLineFav = $derived(
    selectedLine ? favoriteBusRoutes.value.has(selectedLine.routeId) : false,
  )
</script>

<!-- Bus route badge: a bjl Tag carrying the route's own MTA color. `textColor` is set so
     Tag's auto-contrast engine steps aside and the MTA white-on-color pairing is kept
     exactly. A small ★ overlay marks the line as favorited. -->
{#snippet routeBadge(routeId: string, favorited: boolean, size: 'sm' | 'md' = 'md')}
  <div style="position: relative; flex-shrink: 0;">
    <BusRouteBadge {routeId} {size} />
    {#if favorited}
      <span
        style="
          position: absolute; bottom: -3px; right: -3px;
          font-size: 10px; line-height: 1; pointer-events: none;
          color: {Colors.warning}; text-shadow: 0 0 3px rgba(0,0,0,0.9);
        "
      >★</span>
    {/if}
  </div>
{/snippet}

{#snippet starButton(favorited: boolean, onclick: () => void, onLabel: string, offLabel: string)}
  <Button
    variant="text"
    size="sm"
    iconOnly
    {onclick}
    aria-label={favorited ? onLabel : offLabel}
    aria-pressed={favorited}
    title={favorited ? onLabel : offLabel}
    style="flex-shrink: 0; color: {favorited ? Colors.warning : Colors.textSecondary};"
  >{favorited ? '★' : '☆'}</Button>
{/snippet}

<!-- Size and touch target both come from `BACK_ARROW_CHROME`, shared with every other sheet
     that has levels — see its note in ui/theme. -->
{#snippet backButton(onclick: () => void, label: string)}
  <SheetBackButton {onclick} {label} />
{/snippet}

<!-- One bus in a line's detail list: live → countdown + distance; scheduled → departure
     and estimated arrival clock times.

     Only a live bus can be opened: its remaining stops come from a SIRI request keyed by
     vehicle ref, which a scheduled (unmonitored) entry doesn't have. Those stay inert rather
     than offering a tap that would dead-end on an empty list.
     `clock` is set on a transfer: a countdown from now is meaningless to a rider who won't
     be at this stop for another half hour, so the live row shows a wall-clock time. -->
{#snippet busRow(bus: BusArrival, onSelect: (() => void) | undefined, clock: boolean)}
  {@const live = bus.monitored}
  {@const openable = live && !!bus.vehicleRef && !!onSelect}
  <!-- role/tabindex are conditional on `openable`, which the compiler can't see through:
       a scheduled bus is inert, a live one is a real button with a keydown handler. -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div
    onclick={openable ? onSelect : undefined}
    role={openable ? 'button' : undefined}
    tabindex={openable ? 0 : undefined}
    onkeydown={openable
      ? (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onSelect?.() }
      : undefined}
    style="
      display: flex; align-items: center; gap: 10px; padding: 9px 0;
      cursor: {openable ? 'pointer' : 'default'};
    "
  >
    <div style="flex: 1; min-width: 0;">
      <div
        style="
          color: {Colors.text}; {Type.body}; font-weight: 600;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        "
      >{bus.destinationName ? `To ${bus.destinationName}` : 'In service'}</div>
      <div style="display: flex; align-items: center; gap: 6px; margin-top: 2px;">
        <span
          style="
            display: inline-flex; align-items: center; gap: 4px;
            color: {live ? Colors.success : Colors.textMuted}; {Type.caption}; font-weight: 700;
          "
        >
          <span
            style="width: 6px; height: 6px; border-radius: 3px; background-color: {live ? Colors.success : Colors.textMuted};"
          ></span>
          {live ? 'LIVE' : 'SCHEDULED'}
        </span>
        {#if live && bus.distanceText}
          <span style="color: {Colors.textMuted}; {Type.caption};">· {bus.distanceText}</span>
        {/if}
      </div>
    </div>

    {#if live}
      <!-- Live tracked bus → predicted arrival, as a countdown or a clock time. -->
      <div style="text-align: right; flex-shrink: 0;">
        <div style="color: {Colors.text}; font-weight: 800; font-size: {clock ? 15 : 16}px;">
          {clock ? fmtClock(bus.arrivalTime) : fmtMin(bus.arrivalTime)}
        </div>
      </div>
    {:else}
      <!-- Not live → scheduled departure + estimated arrival at this stop. -->
      <div style="text-align: right; flex-shrink: 0;">
        <div style="color: {Colors.text}; {Type.body}; font-weight: 700;">
          Dep {fmtClock(bus.departureTime ?? bus.arrivalTime)}
        </div>
        <div style="color: {Colors.textMuted}; {Type.caption};">
          Arr ~{fmtClock(bus.arrivalTime)}
        </div>
      </div>
    {/if}

    {#if openable}
      <span style="color: {Colors.textSecondary}; font-size: 18px; flex-shrink: 0;">›</span>
    {/if}
  </div>
{/snippet}

{#snippet eyebrow(text: string, marginBottom: number)}
  <SectionEyebrow {text} {marginBottom} />
{/snippet}

<div
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    height: {sheet.height}px;
    background-color: {Colors.ground};
    {panelFrame('top')}
    z-index: 1000;
    display: flex; flex-direction: column;
    transition: {sheet.dragging ? 'none' : 'height 0.22s cubic-bezier(0.4,0,0.2,1)'};
  "
>
  <!-- Top bar: the drag handle, flanked by the only chrome that stays pinned — the same
       arrangement as the station sheet, so ‹ / ★ / ✕ sit on the grabber's line in both and a
       rider switching between a stop and a station reaches for them in one place. The chrome
       costs no extra height by sharing the handle's row. -->
  <div style="position: relative; flex-shrink: 0; display: flex; align-items: center; padding: 4px 18px 2px;">
    <!-- One back affordance, innermost level first: bus timetable → line detail →
         all lines → the nearby list (when the sheet came from there). -->
    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {#if selectedBus}
        {@render backButton(() => { selectedBus = null }, "Back to this line's buses")}
      {:else if selectedKey}
        {@render backButton(() => { selectedKey = null }, 'Back to all lines')}
      {:else if onBack}
        {@render backButton(onBack, 'Back to nearby stops')}
      {/if}
    </div>

    <!-- The drag zone is the empty span *between* the two control clusters. Wrapping the
         buttons in it instead would both invalidate the separator role and let a
         preventDefault'd pointerdown swallow their taps. -->
    <div
      onpointerdown={sheet.onHandleDown}
      role="separator"
      aria-label="Resize bus stop panel"
      style="flex: 1; align-self: stretch; min-height: 34px; cursor: ns-resize; user-select: none; touch-action: none;"
    ></div>

    <!-- Centred on the sheet, not on the drag zone: the two clusters are different widths,
         so only absolute positioning keeps the grabber on the sheet's midline. -->
    <div
      style="
        position: absolute; left: 50%; top: 14px; transform: translateX(-50%);
        width: 36px; height: 4px; border-radius: 2px;
        background-color: rgba(255,255,255,0.25); pointer-events: none;
      "
    ></div>

    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {@render starButton(
        isFavorited.value,
        () => useFavoritesStore.getState().toggleFavoriteBusStop({ id: stop.id, name: stop.name }),
        'Remove from favorites',
        'Add to favorites',
      )}
      <!-- Pulled 9px left. The visible gap between ★ and ✕ is nothing but the two
           buttons' own 18px side padding (36px total); -9px trims that by a quarter
           without shrinking either tap target. -->
      <Button
        variant="text"
        size="sm"
        iconOnly
        onclick={onClose}
        aria-label="Close bus stop panel"
        style="margin-left: -9px;"
      >✕</Button>
    </div>
  </div>

  <!-- Header. A bus stop has no signboard: unlike a station it is a pole on a corner, and
       what identifies it is the code printed on that pole plus the street it stands on. So
       the header leads with the code as an eyebrow, sets the stop name large under it, and
       hangs the lines that call here beneath — the same information the physical pole
       carries, in the same order. With the chrome moved up to the handle row, the name now
       gets the sheet's full width instead of sharing it with three buttons. -->
  <div style="padding: 4px 20px 0; flex-shrink: 0;">
    <div style="color: {Colors.textMuted}; {Type.caption};">Bus stop · {stop.id}</div>
    <!-- A bus stop's name is a cross-street pair ("W 181 ST/FT WASHINGTON AV"), which is
         far longer than a station's one-word signboard, so it is set a step down from
         `Type.screen` and allowed to wrap. Truncating it to "W 181 ST/FT WA…" loses the
         half that tells the rider which corner the pole is on. -->
    <div
      style="
        color: {Colors.text}; {Type.heading}; margin-top: 2px;
        overflow-wrap: anywhere;
      "
    >{stop.name}</div>
  </div>

  <!-- The lines calling here, as a row of route tags directly under the name — the pole's
       own list. Overview only: drilled into one line, the header above already names it and
       repeating every other line would invite a tap that throws the drill-in away. -->
  {#if !selectedBus && !selectedKey && lines.length > 0}
    <div style="display: flex; flex-wrap: wrap; gap: 8px; padding: 12px 20px 0; flex-shrink: 0;">
      {#each lines as l (l.key)}
        {@render routeBadge(l.routeId, favoriteBusRoutes.value.has(l.routeId))}
      {/each}
    </div>
  {/if}

  <!-- Body. Shared by all three drill-down levels, so `drillKey` resets it. -->
  <div
    bind:this={bodyEl}
    style="overflow-y: auto; flex: 1; min-height: 0; padding: 0 20px {safeBottom(28)};"
  >
    {#if selectedBus}
      <!-- Detail: remaining stops of one live bus -->
      <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 10px;">
        {@render routeBadge(selectedBus.routeId, favoriteBusRoutes.value.has(selectedBus.routeId))}
        <span
          style="
            flex: 1; min-width: 0; color: {Colors.text}; {Type.title};
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          "
        >
          {selectedBus.destinationName ? `To ${selectedBus.destinationName}` : selectedBus.routeId}
        </span>
      </div>
      <TripTimetable
        source={{ kind: 'bus', vehicleId: selectedBus.vehicleRef }}
        routeId={selectedBus.routeId}
        currentStopId={stop.id}
        onTransfer={(stopId, stopName, ctx) => onTransfer?.({ id: stopId, name: stopName }, ctx)}
      />
    {:else if loading && !arrivals}
      <div style="display: flex; align-items: center; gap: 8px; padding: 10px 0; color: {Colors.textMuted}; {Type.body};">
        <Spinner size="sm" label="Loading buses" /> Loading buses…
      </div>
    {:else if selectedKey}
      <!-- Detail: next buses for the selected line -->
      <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 8px;">
        {#if selectedLine}
          {@render routeBadge(selectedLine.routeId, favoriteBusRoutes.value.has(selectedLine.routeId))}
        {/if}
        <span style="flex: 1; min-width: 0; color: {Colors.text}; {Type.title};">
          {selectedLine?.destinationName
            ? `To ${selectedLine.destinationName}`
            : selectedLine?.routeId}
        </span>
        {#if selectedLine}
          {@render starButton(
            selectedLineFav,
            () => useFavoritesStore.getState().toggleFavoriteBusRoute(selectedLine.routeId),
            'Remove line from favorites',
            'Add line to favorites',
          )}
        {/if}
      </div>
      {@render eyebrow(
        `Next hour · ${selectedBuses.length || 'no'} bus${selectedBuses.length === 1 ? '' : 'es'}`,
        2,
      )}
      {#if selectedBuses.length > 0}
        <div style="display: flex; flex-direction: column;">
          {#each selectedBuses as b, i (`${b.vehicleRef ?? 'b'}:${i}`)}
            {@render busRow(
              b,
              b.vehicleRef
                ? () => {
                    selectedBus = {
                      vehicleRef: b.vehicleRef!,
                      routeId: b.routeId,
                      destinationName: b.destinationName,
                    }
                  }
                : undefined,
              cutoff != null,
            )}
          {/each}
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          No upcoming buses for this line right now.
        </div>
      {/if}
    {:else}
      <!-- Overview: lines serving this stop -->
      {#if transfer}
        <TransferBanner ctx={transfer} active={!showAll} onToggle={() => { showAll = !showAll }} />
      {/if}
      {#if lines.length > 0}
        <!-- One card, one row per line, ruled between: the redesign's arrivals plate. Each
             row leads with the route tag, says where that bus is going and how far away it
             actually is, and sets the wait at 28px on the trailing edge — the same shape as
             the station sheet's direction cards, so a rider switching between the two reads
             the number in the same place. The whole row opens that line's next hour. -->
        <div style="{cardChrome()} padding: 0 14px; margin-top: 16px;">
          {#each lines as l, i (l.key)}
            <button
              onclick={() => { selectedKey = l.key }}
              aria-label="See the next hour of {l.routeId} buses at this stop"
              style="
                display: flex; align-items: center; gap: 12px; padding: 12px 0;
                background: none; border: none;
                {i === lines.length - 1 ? '' : `border-bottom: ${ROW_RULE};`}
                cursor: pointer; text-align: left; width: 100%;
              "
            >
              {@render routeBadge(l.routeId, favoriteBusRoutes.value.has(l.routeId), 'sm')}
              <div style="flex: 1; min-width: 0;">
                <div
                  style="
                    color: {Colors.text}; {Type.bodyLg};
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                  "
                >{l.destinationName ? `To ${l.destinationName}` : l.routeId}</div>
                <!-- How far off it is, not just when: "2 stops away · 0.4 mi" is what tells
                     a rider whether to keep walking to the corner. Falls back to the live /
                     scheduled distinction when SIRI gave us no distance. -->
                <div
                  style="
                    color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                  "
                >
                  {l.next.monitored
                    ? (l.next.distanceText ?? 'Live')
                    : 'Scheduled · no live data'}
                </div>
              </div>
              <div style="text-align: right; flex-shrink: 0;">
                <!-- Scheduled buses are always clock times; on a transfer the live ones
                     join them, since a countdown from now isn't actionable. -->
                {#if l.next.monitored && cutoff == null}
                  {@const label = fmtMin(l.next.arrivalTime)}
                  {@const isNow = label === 'Now'}
                  <span style="color: {isNow ? Colors.success : Colors.text}; {Type.screen};">
                    {isNow ? label : label.replace(' min', '')}
                  </span>
                  {#if !isNow}
                    <span style="color: {Colors.textSecondary}; {Type.caption};"> min</span>
                  {/if}
                {:else}
                  <span style="color: {Colors.text}; {Type.title};">
                    {fmtClock(l.next.arrivalTime)}
                  </span>
                {/if}
              </div>
              <span aria-hidden="true" style="flex-shrink: 0; color: rgba(255,255,255,0.35); font-size: 15px;">›</span>
            </button>
          {/each}
        </div>
      {:else if cutoff != null}
        <!-- Filtered to nothing. SIRI only predicts ~30 minutes out, so a transfer
             further ahead than that has nothing published yet — not a service gap. -->
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          No buses published yet for after {fmtClock(cutoff)}. Live bus predictions only
          reach about half an hour ahead — check again as you get closer, or tap “Show
          all” to see what’s running now.
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          No buses are currently due at this stop.
        </div>
      {/if}
    {/if}
  </div>
</div>
